<template>
  <v-container class="grey-lighten-3 full-height">
    <!-- Printable Roster Area -->
    <v-row justify="center">
      <v-col cols="12" xl="10" lg="11" md="12">
        <v-sheet class="invoice-paper pa-8 mx-auto" elevation="3" id="printable-area">
          <!-- Header -->
          <div class="header-section d-flex justify-space-between mb-6 lines">
            <div class="company-logo-area d-flex align-center">
              <div class="logo-placeholder mr-4"></div>
              <div>
                <v-img
                  :src="`${getFileServerUrl.replace(/\/$/, '')}/${rosterData?.company_logo || 'uploads/demo_images/user.png'}`"
                  width="200"
                  height="100"
                  class="rounded"
                />
              </div>
            </div>
            <div class="company-address text-caption text-right">
              <h1 class="company-name text-h5 font-weight-bold text-uppercase">
                {{ rosterData?.company_name }}
              </h1>
              <h1 class="text-subtitle-1">
                {{ rosterData?.company_address }}
              </h1>
            </div>
          </div>

          <!-- Title -->
          <div class="text-center mb-6">
            <h1 class="report-title text-h6 font-weight-bold text-decoration-underline">
              Employee Roster
            </h1>
            <h2 class="text-h6 font-weight-bold">
              {{ rosterData?.month }} - {{ rosterData?.year }}
            </h2>
          </div>

          <!-- Roster Table -->
          <div class="table-container mb-6">
            <table class="jv-table">
              <thead>
                <tr>
                  <th class="text-center" style="width: 40px">SL</th>
                  <th>Date</th>
                  <th>Day</th>
                  <th>Code</th>
                  <th>Employee Name</th>
                  <th>Department</th>
                  <th>Shift</th>
                  <th>Shift Type</th>
                  
                </tr>
              </thead>
              <tbody>
                <tr v-for="(row, index) in rosterData.details" :key="index">
                  <td class="text-center">{{ index + 1 }}</td>
                  <td>{{ formattedDate(row.date) }}</td>
                  <td>{{ getDayName(formattedDate(row.date)) }}</td>
                   <td>{{ row.employee?.code }}</td>
                  <td>{{ row.employee?.name }}</td>
                 
                  <td>{{ row?.employee?.department?.name }}</td>
                  <td>{{ row?.shift?.shift_name }}</td>
                  <td>{{ getShiftType(row?.type ?? null) }}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Footer / Notes -->
          <div class="narration-section mb-8">
            <div class="font-weight-bold mb-1">Notes:</div>
            <div class="narration-content lines">
              {{ rosterData?.notes || "No additional notes." }}
            </div>
          </div>
        </v-sheet>
      </v-col>
    </v-row>

    <!-- Action Toolbar -->
    <v-row class="d-print-none mb-4" justify="center">
      <v-col cols="12" md="8" lg="9" class="d-flex justify-center gap-2">
        <v-btn color="success" prepend-icon="mdi-download" @click="downloadPDF">
          Download PDF
        </v-btn>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import formattedDate from "@/plugins/dateFormatter";
import html2pdf from "html2pdf.js";

export default {
  name: "RosterView",
  data() {
    return {
      rosterData: {
        company_name: "",
        company_address: "",
        company_logo: "",
        month: "",
        year: "",
        notes: "",
        details: [],
      },
    };
  },
  computed: {
    getFileServerUrl() {
      return this.$store.getters.getFileServerUrl;
    },
  },
  methods: {
    formattedDate,
    getShiftType(type) {
        const shiftTypes = [
        { label: "Normal", value: "normal" },
        { label: "Leave", value: "leave" },
        { label: "Weekly Off", value: "weekly_off" },
        { label: "Holiday", value: "holiday" },
        { label: "OT", value: "ot" },
        ];

        const found = shiftTypes.find((item) => item.value === type);
        return found ? found.label : "Unknown";
    },

    async fetchRoster(id) {
      try {
        const response = await this.$store.dispatch("getRosterById", {id});
        this.rosterData = response.data.data;
      } catch (error) {
        console.error("Failed to fetch roster:", error);
      }
    },

    async downloadPDF() {
      const element = document.getElementById("printable-area");
      const opt = {
        margin: [0.5, 0.5],
        filename: `employee-roster-${this.rosterData.month}-${this.rosterData.year}.pdf`,
        image: { type: "jpeg", quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true },
        jsPDF: { unit: "cm", format: "a4", orientation: "landscape" },
      };

      try {
        await html2pdf().set(opt).from(element).save();
      } catch (error) {
        console.error("PDF generation failed:", error);
      }
    },

     getDayName(date) {
        if (!date) return "";
        const d = new Date(date);
        return d.toLocaleDateString("en-US", { weekday: "long" }); // Monday, Tuesday ...
    },
  },

  async mounted() {
    const rosterId = this.$route.params.id;
    if (rosterId) {
        await this.fetchRoster(rosterId);
    }
  },
};
</script>

<style scoped>
/* Similar styling as your Journal Voucher template */
.signature-image {
  width: 50px;
  height: auto;
  margin-left: 8px;
}

.full-height {
  min-height: 100vh;
}

.grey-lighten-3 {
  background-color: #eeeeee !important;
}

.invoice-paper {
  background-color: white;
  color: black;
  font-family: "Times New Roman", serif;
  width: 100%;
  min-height: 297mm;
}

.jv-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
  table-layout: fixed;
}

.jv-table th,
.jv-table td {
  border: 1px solid #000;
  padding: 6px 4px;
  text-align: left;
}

.narration-content {
  border-bottom: 1px solid #ccc;
  min-height: 60px;
  padding: 5px 0;
  white-space: pre-wrap;
}

.lines {
  border-bottom: 1px solid #ccc;
  padding-bottom: 5px;
}

/* Print styles */
@media print {
  body * {
    visibility: hidden;
  }

  #printable-area,
  #printable-area * {
    visibility: visible;
  }

  #printable-area {
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
  }

  .d-print-none {
    display: none !important;
  }
}
</style>
